import React, { useState } from 'react';
import { getMerchantAppOrigin } from '@/utils/appConfig';
import { 
  Store, 
  ArrowRight, 
  X,
  Sparkles,
  Package,
  CheckCircle,
  Clock,
  Zap
} from 'lucide-react';
import { Card, CardContent } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Badge } from '@vaiu/ui/badge';
import { Progress } from '@vaiu/ui/progress';
import { useMerchantStatus } from '@/hooks/useMerchantStatus';
import { useAuth } from '@/context/AuthContext';

interface MarketplaceSetupReminderProps {
  onDismiss?: () => void;
}

const MarketplaceSetupReminder: React.FC<MarketplaceSetupReminderProps> = ({ onDismiss }) => {
  const { user } = useAuth();
  const status = useMerchantStatus();
  const [dismissed, setDismissed] = useState(false);

  // Check if already dismissed today
  React.useEffect(() => {
    if (user) {
      const dismissedKey = `marketplace_reminder_dismissed_${user._id}_${new Date().toDateString()}`;
      const wasDismissed = sessionStorage.getItem(dismissedKey);
      if (wasDismissed) {
        setDismissed(true);
      }
    }
  }, [user]);

  const handleDismiss = () => {
    if (user) {
      const dismissedKey = `marketplace_reminder_dismissed_${user._id}_${new Date().toDateString()}`;
      sessionStorage.setItem(dismissedKey, 'true');
    }
    setDismissed(true);
    if (onDismiss) {
      onDismiss();
    }
  };

  const handleQuickSetup = () => {
    window.location.assign(`${getMerchantAppOrigin()}/merchant/fast-track`);
  };

  const handleManualSetup = () => {
    window.location.assign(`${getMerchantAppOrigin()}/profile/merchant`);
  };

  // Don't show if:
  // - Not a merchant
  // - Has complete business info (minimum for directory listing)
  // - Still loading
  // - Was dismissed
  // - Already published or completed onboarding (from user.otherProfiles.merchant)
  const merchantProfile = user?.otherProfiles?.merchant;
  const alreadySetup = merchantProfile?.isPublished || merchantProfile?.completedOnboarding;
  
  if (!user?.roles?.includes('MERCHANT') || 
      status.hasBusinessInfo || 
      alreadySetup ||
      status.loading || 
      dismissed) {
    return null;
  }

  // Calculate what's missing - simplified to just business info
  const setupSteps = [
    { label: 'Business Information', completed: status.hasBusinessInfo, icon: Store },
  ];
  
  const completedSteps = setupSteps.filter(step => step.completed).length;
  const progressPercentage = status.hasBusinessInfo ? 100 : 0;

  return (
    <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 overflow-hidden">
      <CardContent className="p-4 sm:p-6">
        <div className="relative">
          {/* Close button - absolute positioned */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            className="absolute -top-2 -right-2 text-gray-400 hover:text-gray-600 z-10"
          >
            <X className="w-4 h-4" />
          </Button>
          
          <div className="pr-8">
            {/* Header */}
            <div className="flex items-center space-x-3 mb-3">
              <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                <Store className="w-5 h-5 text-blue-600" />
              </div>
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 truncate">
                  Get listed in marketplace search
                </h3>
                <p className="text-sm text-gray-600">
                  Complete your business profile so customers can find you in the directory
                </p>
              </div>
            </div>

            {/* Progress Indicator */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">
                  Setup Progress: {completedSteps} of {setupSteps.length} steps
                </span>
                <Badge variant="outline" className="bg-white">
                  {Math.round(progressPercentage)}% Complete
                </Badge>
              </div>
              <Progress value={progressPercentage} className="h-2" />
              <div className="grid grid-cols-2 gap-2 mt-3">
                {setupSteps.map((step, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    {step.completed ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <Clock className="w-4 h-4 text-gray-400" />
                    )}
                    <span className={`text-sm ${
                      step.completed ? 'text-gray-700' : 'text-gray-500'
                    }`}>
                      {step.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* AI Setup Promotion */}
            <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg p-3 mb-4 border border-purple-200">
              <div className="flex items-start space-x-2">
                <Sparkles className="w-5 h-5 text-purple-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-purple-900">
                    Quick Setup with Google Maps!
                  </p>
                  <p className="text-xs text-purple-700 mt-1">
                    Import your business info from Google Maps
                  </p>
                </div>
              </div>
            </div>

            {/* Benefits */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4 text-sm">
              <div className="flex items-center space-x-1 text-gray-700">
                <Zap className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                <span className="truncate">Get discovered</span>
              </div>
              <div className="flex items-center space-x-1 text-gray-700">
                <Store className="w-4 h-4 text-blue-500 flex-shrink-0" />
                <span className="truncate">Public profile</span>
              </div>
              <div className="flex items-center space-x-1 text-gray-700">
                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                <span className="truncate">Build trust</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
              <Button 
                onClick={handleQuickSetup}
                className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white flex-shrink-0"
              >
                <Sparkles className="w-4 h-4 mr-1" />
                <span className="truncate">Quick Setup</span>
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <Button 
                variant="outline"
                onClick={handleManualSetup}
                className="border-blue-300 hover:bg-blue-50 flex-shrink-0"
              >
                Manual Setup
              </Button>
              <Button
                variant="ghost"
                onClick={handleDismiss}
                className="text-gray-500 hover:text-gray-700 flex-shrink-0"
              >
                Later
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MarketplaceSetupReminder;
